// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut as firebaseSignOut,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../services/firebase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [jeweller, setJeweller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sign in function
  const signIn = async (email, password) => {
    setError(null);
    if (password.startsWith('eyJ')) {
      console.log("Detected custom token, signing in with token");
      try {
        return await signInWithAdminToken(password);
      } catch (error) {
        console.error("Token sign in error:", error);
        throw new Error('Invalid admin token');
      }
    }

    console.log("Attempting to sign in with:", email);
    
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log("Sign in successful:", user.uid);
      
      // Immediately check if this user is a jeweller
      const jewellerDocRef = doc(db, 'jewellers', user.uid);
      const jewellerDoc = await getDoc(jewellerDocRef);
      
      if (!jewellerDoc.exists()) {
        // User exists but is not a jeweller - sign them out
        await firebaseSignOut(auth);
        throw new Error('This account does not have jeweller portal access. Please contact support.');
      }
      
      const jewellerData = jewellerDoc.data();
      
      // Check if portal is enabled
      if (!jewellerData.portalEnabled) {
        await firebaseSignOut(auth);
        throw new Error('Your jeweller portal access is not enabled. Please contact support.');
      }
      
      // ✅ UPDATE lastLoginAt AFTER SUCCESSFUL LOGIN
      try {
        // Get the current ID token to check for admin claims
        const idTokenResult = await user.getIdTokenResult();
        const isAdminViewing = idTokenResult.claims.adminViewing === true;
        
        // Only update lastLoginAt if this is NOT an admin viewing
        if (!isAdminViewing) {
          await updateDoc(jewellerDocRef, {
            lastLoginAt: serverTimestamp()
          });
          console.log("✅ Successfully updated lastLoginAt for jeweller:", user.uid);
        } else {
          console.log("⚠️ Skipping lastLoginAt update - admin is viewing this portal");
        }
      } catch (updateError) {
        // Log the error but don't fail the login if the update fails
        console.error("Failed to update lastLoginAt:", updateError);
      }
      
      return user;
    } catch (error) {
      console.error("Sign in error:", error);
      
      // Handle specific Firebase auth errors
      let errorMessage = error.message || 'Failed to sign in. Please check your credentials.';
      
      if (error.code) {
        switch (error.code) {
          case 'auth/user-not-found':
          case 'auth/wrong-password':
          case 'auth/invalid-credential':
            errorMessage = 'Invalid email or password.';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Please enter a valid email address.';
            break;
          case 'auth/user-disabled':
            errorMessage = 'Your account has been disabled. Please contact support.';
            break;
          case 'auth/too-many-requests':
            errorMessage = 'Too many unsuccessful login attempts. Please try again later.';
            break;
        }
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Sign in with admin token (for admin "View As" feature)
  const signInWithAdminToken = async (customToken) => {
    setError(null);
    console.log("Signing in with admin token");
    
    try {
      const userCredential = await signInWithCustomToken(auth, customToken);
      const user = userCredential.user;
      console.log("Admin token sign in successful:", user.uid);
      
      // Fetch jeweller data
      const jewellerDocRef = doc(db, 'jewellers', user.uid);
      const jewellerDoc = await getDoc(jewellerDocRef);
      
      if (!jewellerDoc.exists()) {
        await firebaseSignOut(auth);
        throw new Error('Jeweller account not found.');
      }
      
      return user;
    } catch (error) {
      console.error("Admin token sign in error:", error);
      setError('Failed to sign in with admin token.');
      throw error;
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setJeweller(null);
      setError(null);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Reset password function
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      console.error('Error sending password reset:', error);
      throw error;
    }
  };

  // Fetch jeweller data
  const fetchJewellerData = async (userId) => {
    try {
      console.log('Fetching jeweller data for:', userId);
      const jewellerDocRef = doc(db, 'jewellers', userId);
      const jewellerDoc = await getDoc(jewellerDocRef);
      
      if (jewellerDoc.exists()) {
        const data = jewellerDoc.data();
        console.log('Jeweller data found:', data.companyName);
        setJeweller({ 
          id: jewellerDoc.id, 
          ...data 
        });
        return true;
      } else {
        console.log('No jeweller document found for user:', userId);
        // User exists in Auth but not as a jeweller
        setError('This account does not have jeweller portal access.');
        await signOut();
        return false;
      }
    } catch (error) {
      setError('Failed to load jeweller data. Please try again later.');
      console.error('Error fetching jeweller data:', error);
      return false;
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("Auth state changed. User:", user?.uid);
      
      if (user) {
        setCurrentUser(user);
        const isJeweller = await fetchJewellerData(user.uid);
        if (!isJeweller) {
          setCurrentUser(null);
        }
      } else {
        setCurrentUser(null);
        setJeweller(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const value = {
    currentUser,
    jeweller,
    loading,
    error,
    signIn,
    signInWithAdminToken,
    signOut,
    resetPassword,
    setError,
    refreshJewellerData: () => fetchJewellerData(currentUser?.uid)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};